import { useAccount, useBalance, useReadContract } from "wagmi";
import { type Address } from "viem";
import { ERC20_ABI } from "../config/contracts";

export function useTokenBalance(tokenAddress: Address | undefined) {
  const { address: account } = useAccount();
  const isNative =
    !tokenAddress || tokenAddress === "0x0000000000000000000000000000000000000000";

  const nativeBalance = useBalance({
    address: account,
    query: {
      enabled: !!account && isNative,
      refetchInterval: 8_000,
    },
  });

  const tokenBalance = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: "balanceOf",
    args: account ? [account] : undefined,
    query: {
      enabled: !!account && !isNative && !!tokenAddress,
      refetchInterval: 8_000,
    },
  });

  const tokenDecimals = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: "decimals",
    query: {
      enabled: !isNative && !!tokenAddress,
      staleTime: Infinity,
    },
  });

  if (isNative) {
    return {
      balance: nativeBalance.data?.value ?? 0n,
      decimals: 18,
      isLoading: nativeBalance.isLoading,
      refetch: nativeBalance.refetch,
    };
  }

  return {
    balance: (tokenBalance.data as bigint) ?? 0n,
    decimals: (tokenDecimals.data as number) ?? 18,
    isLoading: tokenBalance.isLoading,
    refetch: tokenBalance.refetch,
  };
}

export function useTokenDecimals(tokenAddress: Address | undefined) {
  const isNative =
    !tokenAddress || tokenAddress === "0x0000000000000000000000000000000000000000";

  const { data } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: "decimals",
    query: {
      enabled: !isNative && !!tokenAddress,
      staleTime: Infinity,
    },
  });

  return isNative ? 18 : (data as number) ?? 18;
}

export function useTokenInfo(tokenAddress: Address | undefined) {
  const isNative =
    !tokenAddress || tokenAddress === "0x0000000000000000000000000000000000000000";

  const nameResult = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: "name",
    query: { enabled: !isNative && !!tokenAddress, staleTime: Infinity },
  });

  const symbolResult = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: "symbol",
    query: { enabled: !isNative && !!tokenAddress, staleTime: Infinity },
  });

  const decimalsResult = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: "decimals",
    query: { enabled: !isNative && !!tokenAddress, staleTime: Infinity },
  });

  if (isNative) {
    return {
      name: "JASMY",
      symbol: "JASMY",
      decimals: 18,
      isLoading: false,
    };
  }

  return {
    name: (nameResult.data as string) ?? "",
    symbol: (symbolResult.data as string) ?? "",
    decimals: (decimalsResult.data as number) ?? 18,
    isLoading: nameResult.isLoading || symbolResult.isLoading || decimalsResult.isLoading,
  };
}
